/**
 * Inside the Android app: tell people when this server offers a newer app (the web app
 * itself updates with the server, but the app around it needs installing).
 *
 * App 1.3+ downloads, checks and opens the installer itself ("Update"); older builds get the
 * APK as a download (from the notification, tap to install).
 */
import { create } from 'zustand';
import type { AndroidRelease } from '@comms/shared';
import { api } from './api';
import {
  androidAppInstallsUpdates,
  androidAppVersion,
  isAndroidApp,
  nativeApp,
  type UpdateState,
} from './native';

export type AppRelease = Extract<AndroidRelease, { available: true }>;

const CHECK_EVERY_MS = 6 * 60 * 60_000;
const LATER_FOR_MS = 24 * 60 * 60_000;
const LATER = 'app-update.later';
export const APK_URL = '/download/Comms.apk';

export const useAppUpdate = create<{
  available: AppRelease | null;
  state: UpdateState | null;
  message: string | null;
}>(() => ({ available: null, state: null, message: null }));

/** Is `release` newer than the app build that's running? */
export const newerApp = (release: AndroidRelease, running: number): release is AppRelease =>
  release.available && running > 0 && release.versionCode > running;

type Later = { versionCode: number; until: number };

function laterPref(): Later | null {
  try {
    return JSON.parse(localStorage.getItem(LATER) ?? 'null') as Later | null;
  } catch {
    return null;
  }
}

/** "Later": hide this version's prompt for a day. */
export function remindLater(versionCode: number, now = Date.now()) {
  try {
    localStorage.setItem(LATER, JSON.stringify({ versionCode, until: now + LATER_FOR_MS }));
  } catch {
    /* ignore */
  }
  useAppUpdate.setState({ available: null, state: null, message: null });
}

export function snoozed(versionCode: number, now = Date.now()) {
  const l = laterPref();
  return !!l && l.versionCode === versionCode && now < l.until;
}

export async function checkForAppUpdate() {
  let release: AndroidRelease;
  try {
    release = await api<AndroidRelease>('GET', '/android/latest');
  } catch {
    return;
  }
  if (!newerApp(release, androidAppVersion()) || snoozed(release.versionCode)) return;
  if (useAppUpdate.getState().available?.versionCode === release.versionCode) return;
  useAppUpdate.setState({ available: release, state: null, message: null });
}

export function installAppUpdate() {
  const release = useAppUpdate.getState().available;
  if (!release) return;
  if (androidAppInstallsUpdates()) {
    useAppUpdate.setState({ state: 'downloading', message: null });
    nativeApp.installUpdate(release.sha256);
  } else {
    // The app saves it to Downloads; tapping the finished download installs it.
    window.location.assign(APK_URL);
    useAppUpdate.setState({ state: 'downloading', message: null });
  }
}

/** What the app reports while updating. */
export function onUpdateState(state: UpdateState, message?: string) {
  useAppUpdate.setState({ state, message: message ?? null });
}

let started = false;

export function startAppUpdateChecks() {
  if (started || !isAndroidApp()) return;
  started = true;
  void checkForAppUpdate();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void checkForAppUpdate();
  });
  window.setInterval(() => void checkForAppUpdate(), CHECK_EVERY_MS);
}
